/**
 * Service worker registration and update handling.
 *
 * The worker itself lives in src/service-worker/sw.js and is copied to /sw.js at build
 * time. A worker is what makes the app installable on Android and what keeps it usable
 * when the connection drops, so registration failures are logged but never fatal.
 */

/** How often an open (installed) app checks whether a new version was deployed. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000

type UpdateListener = () => void

let registration: ServiceWorkerRegistration | undefined
let waitingWorker: ServiceWorker | undefined
let reloading = false
const listeners = new Set<UpdateListener>()

const isSupported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator

const notifyUpdateAvailable = (worker: ServiceWorker) => {
  waitingWorker = worker
  for (const listener of listeners) {
    listener()
  }
}

/** Subscribe to "a new version is installed and waiting". Returns an unsubscribe function. */
export const onServiceWorkerUpdate = (listener: UpdateListener): (() => void) => {
  listeners.add(listener)
  if (waitingWorker) {
    listener()
  }
  return () => {
    listeners.delete(listener)
  }
}

export const hasServiceWorkerUpdate = (): boolean => Boolean(waitingWorker)

/**
 * Activates the waiting worker and reloads once it has taken over. Reloading is what
 * swaps in the new bundles, so this is only ever called from an explicit user action.
 */
export const applyServiceWorkerUpdate = (): void => {
  if (!waitingWorker) {
    window.location.reload()
    return
  }
  waitingWorker.postMessage({ type: 'SKIP_WAITING' })
}

const trackInstallingWorker = (worker: ServiceWorker | null) => {
  if (!worker) {
    return
  }
  worker.addEventListener('statechange', () => {
    // A worker that installs while another one controls the page is an update.
    // Without a controller it is the very first install, which needs no prompt.
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      notifyUpdateAvailable(worker)
    }
  })
}

const checkForUpdate = () => {
  registration?.update().catch(() => {
    // offline or the server is unreachable, we retry on the next interval
  })
}

export const registerServiceWorker = (): void => {
  if (!isSupported()) {
    return
  }

  // Development runs against webpack-dev-server, where a caching worker only gets in the
  // way. Unregister so machines that once ran a production build locally are cleaned up.
  if (process.env.NODE_ENV !== 'production') {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const existing of registrations) {
        existing.unregister()
      }
    })
    return
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) {
      return
    }
    reloading = true
    window.location.reload()
  })

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((serviceWorkerRegistration) => {
        registration = serviceWorkerRegistration

        if (serviceWorkerRegistration.waiting && navigator.serviceWorker.controller) {
          notifyUpdateAvailable(serviceWorkerRegistration.waiting)
        }

        serviceWorkerRegistration.addEventListener('updatefound', () => {
          trackInstallingWorker(serviceWorkerRegistration.installing)
        })

        setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS)
        // An installed app is often resumed rather than opened, so a visibility change
        // is the closest thing we get to "the user came back".
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            checkForUpdate()
          }
        })
      })
      .catch((error) => {
        console.error('Service worker registration failed', error)
      })
  })
}
